import CardUtils from '../../../utils/CardUtils';

const getCardValue = (rank) => {
  const values = {
    '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10,
    'J': 11, 'Q': 12, 'K': 13, 'A': 14, 'Joker': 15
  };
  return values[rank] || 0;
};

function isGroupValid(cards, groupType, wildJoker = null) {
  if (cards.length < 3) return false;
  
  // Separate jokers from natural cards
    const jokers = cards.filter(card => {
    if (card.isJoker || card.suit === 'joker' || card.rank === 'Joker') return true;
    // ALL cards of the wild joker rank are wild jokers, including the wild joker card itself
    // (e.g., wild joker = 6♦, then ALL 6s including 6♦ are wild jokers)
    if (wildJoker && card.rank === wildJoker.rank) return true;
    return false;
  });
  const nonJokerCards = cards.filter(card => !jokers.includes(card));
  
  if (nonJokerCards.length === 0) {
    // All cards are jokers — no natural card to form a valid sequence
    return false;
  }
  
  const suits = [...new Set(nonJokerCards.map(card => card.suit))];
  if (suits.length > 1) return false;
  
  const values = nonJokerCards.map(card => card.value || getCardValue(card.rank)).sort((a, b) => a - b);
  
  // Check if natural cards are already strictly consecutive (pure sequence)
  let isPure = true;
  for (let i = 1; i < values.length; i++) {
    if (values[i] !== values[i - 1] + 1) {
      isPure = false;
      break;
    }
  }
  if (isPure) return true;
  
  // If jokers are present, check if they can fill the gaps
  const jokerCount = jokers.length;
  if (jokerCount === 0) return false;
  
  // Calculate gaps between consecutive natural cards
  let totalGaps = 0;
  for (let i = 1; i < values.length; i++) {
    const gap = values[i] - values[i - 1] - 1;
    if (gap > 0) totalGaps += gap;
    if (gap < 0) return false; // duplicate values
  }
  
  // If gaps can be filled by jokers, it's a valid impure sequence
  if (totalGaps <= jokerCount) return true;
  
  // Try Ace-as-low: if Ace (14) is present, treat it as value 1
  if (values[values.length - 1] === 14 && values[0] >= 2) {
    const adjustedValues = values.map(v => v === 14 ? 1 : v).sort((a, b) => a - b);
    let adjustedGaps = 0;
    for (let i = 1; i < adjustedValues.length; i++) {
      const gap = adjustedValues[i] - adjustedValues[i - 1] - 1;
      if (gap > 0) adjustedGaps += gap;
    }
    if (adjustedGaps <= jokerCount) return true;
  }
  
  return false;
}

export const autoArrangeCards = (cards, wildJoker) => {
  if (!cards || cards.length === 0) return { arrangedCards: [], groupStructure: {} };
  
  console.log('Auto arrange called with cards:', cards.length);
  
  const flatCards = [];
  cards.forEach(card => {
    if (card.isGroup) {
      card.cards.forEach(groupCard => {
        flatCards.push({...groupCard});
      });
    } else if (!card.isGroupSeparator && !card.isPlaceholder) {
      flatCards.push({...card});
    }
  });

  console.log('Flat cards after processing:', flatCards.length);

  const groups = {
    hearts: [],
    spades: [], 
    diamonds: [],
    clubs: [],
    jokers: []
  };

        flatCards.forEach(card => {
    // A card is a joker if:
    //   1. It's a printed joker (isJoker flag or suit === 'joker')
    //   2. It matches the wild joker rank (e.g., wild joker = 6♦, then ALL 6s are jokers)
    const isJokerCard = card.isJoker || card.rank === 'Joker' || card.suit === 'joker' || (wildJoker && card.rank === wildJoker.rank);
    
    if (isJokerCard) {
      groups.jokers.push(card);
    } else {
      switch(card.suit) {
        case 'hearts': groups.hearts.push(card); break;
        case 'spades': groups.spades.push(card); break;
        case 'diamonds': groups.diamonds.push(card); break;
        case 'clubs': groups.clubs.push(card); break;
        default: groups.jokers.push(card);
      }
    }
  });

  Object.values(groups).forEach(group => {
    group.sort((a, b) => {
      const aValue = a.value || getCardValue(a.rank);
      const bValue = b.value || getCardValue(b.rank);
      return aValue - bValue;
    });
  });

  const arrangedCards = [];
    const groupOrder = ['hearts', 'spades', 'diamonds', 'clubs', 'jokers'];

  groupOrder.forEach((groupKey) => {
    const groupCards = groups[groupKey];
    
    if (groupCards.length > 0) {
      const group = {
        isGroup: true,
        cards: groupCards,
        id: `auto-group-${groupKey}-${Date.now()}`,
        groupType: groupKey,
        isValid: isGroupValid(groupCards, groupKey, wildJoker),
        cardCount: groupCards.length,
        createdAt: Date.now()
      };
      
      arrangedCards.push(group);
    }
  });

  // A group needs at least 2 cards: any single-card group (e.g. one lone club) is moved into
  // the LAST group, so the hand never shows one-card groups and takes less space.
  const singleGroups = arrangedCards.filter(g => g.isGroup && g.cards.length < 2);
  if (singleGroups.length > 0) {
    const multiGroups = arrangedCards.filter(g => g.isGroup && g.cards.length >= 2);
    const singleCards = singleGroups.flatMap(g => g.cards);
    let target = null;
    if (multiGroups.length > 0) {
      target = multiGroups[multiGroups.length - 1];
      target.cards = [...target.cards, ...singleCards];
    } else if (arrangedCards.length > 0) {
      // Every group is a single card: put them all together in one group
      target = arrangedCards[arrangedCards.length - 1];
      target.cards = singleGroups.flatMap(g => g.cards);
    }
    if (target) {
      target.cardCount = target.cards.length;
      target.isValid = isGroupValid(target.cards, target.groupType, wildJoker);
      const keep = arrangedCards.filter(g => !(g.isGroup && g.cards.length < 2 && g !== target));
      arrangedCards.length = 0;
      keep.forEach(g => arrangedCards.push(g));
    }
  }

  console.log('Final arranged cards:', arrangedCards.length);
  console.log('Groups created:', arrangedCards.filter(card => card.isGroup).length);
  console.log('Cards in each group:');
  arrangedCards.forEach(group => {
    console.log(`- ${group.groupType}: ${group.cards.length} cards`);
  });

  return {
    arrangedCards,
    groupStructure: groups,
    hasSeparators: false
  };
};

export const manualArrangeCards = (cards, draggedCardId, targetPosition) => {
  const cardIndex = cards.findIndex(card => card.id === draggedCardId);
  if (cardIndex === -1) return cards;
  
  const realCards = cards.filter(card => 
    !card.isGroupSeparator && !card.isPlaceholder && !card.isGroup
  );
  
  const groupCards = cards.filter(card => card.isGroup);
  
  const newCards = [...realCards, ...groupCards];
  const [draggedCard] = newCards.splice(cardIndex, 1);
  
  let insertIndex = targetPosition;
  if (insertIndex > newCards.length) insertIndex = newCards.length;
  if (insertIndex < 0) insertIndex = 0;
  
  newCards.splice(insertIndex, 0, {
    ...draggedCard,
    arrangementIndex: insertIndex
  });
  
  return newCards;
};

export const createCardGroup = (selectedCards, playerCards) => {
  if (selectedCards.length < 2) {
    console.log('Need at least 2 cards to form a group');
    return playerCards;
  }
  
  const validation = isValidGroup(selectedCards);
  if (!validation.isValid) {
    console.log('Selected cards cannot form a valid group:', validation.reason);
    return playerCards;
  }
  
  const remainingCards = playerCards.filter(card => 
    !selectedCards.some(selected => selected.id === card.id) && 
    !card.isGroupSeparator
  );
  
  const newGroup = {
    isGroup: true,
    cards: [...selectedCards],
    id: `group-${Date.now()}`,
    groupType: validation.type,
    isValid: true,
    createdAt: Date.now(),
    arrangementIndex: remainingCards.length
  };
  
  console.log(`Created new ${newGroup.groupType} group with ${selectedCards.length} cards`);
  
  return insertGroupAtPosition(remainingCards, newGroup);
};

const insertGroupAtPosition = (cards, newGroup) => {
  const lastGroupIndex = cards.reduce((lastIndex, card, index) => 
    card.isGroup ? index : lastIndex, -1
  );
  
  if (lastGroupIndex !== -1) {
    return [
      ...cards.slice(0, lastGroupIndex + 1),
      newGroup,
      ...cards.slice(lastGroupIndex + 1)
    ];
  }
  
  const firstSeparatorIndex = cards.findIndex(card => card.isGroupSeparator);
  if (firstSeparatorIndex !== -1) {
    return [
      ...cards.slice(0, firstSeparatorIndex),
      newGroup,
      ...cards.slice(firstSeparatorIndex)
    ];
  }
  
  return [...cards, newGroup];
};

export const isValidGroup = (cards, wildJoker = null) => {
  if (cards.length < 2) {
    return { isValid: false, reason: 'Need at least 2 cards' };
  }
  
    // Separate jokers from natural cards
  // A card is a joker if:
  //   1. Printed joker (isJoker flag, suit === 'joker', rank === 'Joker')
  //   2. Wild joker — ALL cards matching the wild joker rank, INCLUDING the wild joker card itself
  //      (e.g., wild joker = 6♦, then 6♦, 6♠, 6♥, 6♣ are all jokers)
  const isJokerCard = (card) => {
    if (card.isJoker || card.rank === 'Joker' || card.suit === 'joker') return true;
    if (wildJoker && card.rank === wildJoker.rank) return true;
    return false;
  };
  
  const jokers = cards.filter(card => isJokerCard(card));
  const naturalCards = cards.filter(card => !isJokerCard(card));
  
  // If all cards are jokers, invalid
  if (naturalCards.length === 0) {
    return { isValid: false, reason: 'At least one natural card required' };
  }
  
  const jokerCount = jokers.length;
  
  // Check if it's a SET (same rank, different suits)
  const uniqueRanks = [...new Set(naturalCards.map(card => card.rank))];
  if (uniqueRanks.length === 1) {
    // Same rank — valid set. Jokers can substitute for missing suits.
    // e.g., 2H, 2D + joker = valid set (joker stands for 2C or 2S)
    return { isValid: true, type: 'set' };
  }
  
  // Check if it's a SEQUENCE (same suit, consecutive values with jokers filling gaps)
  const uniqueSuits = [...new Set(naturalCards.map(card => card.suit))];
  if (uniqueSuits.length !== 1) {
    // Different suits among natural cards AND different ranks — invalid
    return { isValid: false, reason: 'Cards have different suits and ranks — not a valid set or sequence' };
  }
  
  // Same suit — check if jokers can fill gaps to form a valid sequence
  const values = naturalCards
    .map(card => card.value || getCardValue(card.rank))
    .sort((a, b) => a - b);
  
  // Calculate gaps between consecutive natural cards
  let totalGaps = 0;
  for (let i = 1; i < values.length; i++) {
    const gap = values[i] - values[i - 1] - 1;
    if (gap < 0) {
      // Duplicate value (can't have two same values in a sequence)
      return { isValid: false, reason: 'Duplicate card values in same suit' };
    }
    totalGaps += gap;
  }
  
    // For a valid sequence: total gaps must be <= number of jokers
  if (totalGaps <= jokerCount) {
    return { isValid: true, type: 'sequence' };
  }
  
  // Try Ace-as-low: check if Ace (14) can be treated as 1
  if (values.includes(14)) {
    // Try treating Ace as 1: replace 14 with 1 and re-sort
    const adjustedValues = values.map(v => v === 14 ? 1 : v).sort((a, b) => a - b);
    let adjustedGaps = 0;
    for (let i = 1; i < adjustedValues.length; i++) {
      const gap = adjustedValues[i] - adjustedValues[i - 1] - 1;
      if (gap >= 0) adjustedGaps += gap;
    }
    if (adjustedGaps <= jokerCount) {
      return { isValid: true, type: 'sequence' };
    }
  }
  
  const needed = totalGaps - jokerCount;
  return { isValid: false, reason: `Cannot form sequence — need ${needed} more card${needed > 1 ? 's' : ''} to fill gap${totalGaps > 1 ? 's' : ''}` };
};

export const toggleCardSelection = (cardId, selectedCards) => {
  if (selectedCards.includes(cardId)) {
    return selectedCards.filter(id => id !== cardId);
  } else {
    return [...selectedCards, cardId];
  }
};

export const createGroupFromSelectedCards = (selectedCardIds, playerCards, maxGroups = 6) => {
  console.log('Creating group from selected cards:', selectedCardIds, 'from', playerCards);
  
  if (selectedCardIds.length < 2) {
    return { 
      success: false, 
      message: 'Select at least 2 cards to form a group',
      updatedCards: playerCards 
    };
  }
  
  const currentGroups = playerCards.filter(card => card.isGroup).length;
  if (currentGroups >= maxGroups) {
    return { 
      success: false, 
      message: 'Maximum allowed group is 6',
      updatedCards: playerCards 
    };
  }
  
  const { selectedCards, remainingCards } = extractSelectedCards(selectedCardIds, playerCards);
  
  if (selectedCards.length < 2) {
    return { 
      success: false, 
      message: 'Could not find selected cards',
      updatedCards: playerCards 
    };
  }
  
  const newGroup = {
    isGroup: true,
    cards: selectedCards,
    id: `manual-group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    groupType: 'manual',
    isValid: true,
    createdAt: Date.now(),
    cardCount: selectedCards.length
  };
  
  console.log('Created new group:', newGroup);
  
  const groupInsertIndex = findGroupInsertIndex(remainingCards);
  const updatedCards = insertGroupAtIndex(remainingCards, newGroup, groupInsertIndex);
  
  return {
    success: true,
    message: `Created group with ${selectedCards.length} cards`,
    updatedCards: updatedCards,
    selectedCards: []
  };
};

const extractSelectedCards = (selectedCardIds, playerCards) => {
  const selectedCards = [];
  const remainingCards = [];
  
  const extractFromGroup = (group, cardIds) => {
    const remainingGroupCards = [];
    group.cards.forEach(card => {
      if (cardIds.includes(card.id)) {
        selectedCards.push(card);
      } else {
        remainingGroupCards.push(card);
      }
    });
    
    if (remainingGroupCards.length > 0) {
      return {
        ...group,
        cards: remainingGroupCards,
        cardCount: remainingGroupCards.length
      };
    }
    return null;
  };
  
  playerCards.forEach(card => {
    if (card.isGroup) {
      const updatedGroup = extractFromGroup(card, selectedCardIds);
      if (updatedGroup) {
        remainingCards.push(updatedGroup);
      }
    } else {
      if (selectedCardIds.includes(card.id)) {
        selectedCards.push(card);
      } else {
        remainingCards.push(card);
      }
    }
  });
  
  return { selectedCards, remainingCards };
};

const findGroupInsertIndex = (cards) => {
  let lastGroupIndex = -1;
  cards.forEach((card, index) => {
    if (card.isGroup) {
      lastGroupIndex = index;
    }
  });
  return lastGroupIndex + 1;
};

const insertGroupAtIndex = (cards, newGroup, index) => {
  if (index >= cards.length) {
    return [...cards, newGroup];
  } else if (index <= 0) {
    return [newGroup, ...cards];
  } else {
    return [
      ...cards.slice(0, index),
      newGroup,
      ...cards.slice(index)
    ];
  }
};

export const autoGroupSingleCards = (playerCards, maxGroups = 6) => {
  console.log('Auto-grouping single cards...');
  
  const currentGroups = playerCards.filter(card => card.isGroup).length;
  if (currentGroups >= maxGroups) {
    console.log('Max groups reached, cannot auto-group singles');
    return playerCards;
  }
  
  const singleCards = [];
  const groups = [];
  
  playerCards.forEach(card => {
    if (card.isGroup) {
      groups.push(card);
    } else {
      singleCards.push(card);
    }
  });
  
  if (singleCards.length >= 2) {
    console.log(`Found ${singleCards.length} single cards, creating auto-group`);
    
    const autoGroup = {
      isGroup: true,
      cards: singleCards,
      id: `auto-singles-group-${Date.now()}`,
      groupType: 'singles',
      isValid: false,
      createdAt: Date.now(),
      cardCount: singleCards.length
    };
    
    return [...groups, autoGroup];
  }
  
  if (singleCards.length === 1 && groups.length > 0) {
    console.log('Adding single card to last group');
    
    const lastGroup = groups[groups.length - 1];
    const updatedLastGroup = {
      ...lastGroup,
      cards: [...lastGroup.cards, singleCards[0]],
      cardCount: lastGroup.cards.length + 1
    };
    
    const updatedGroups = [...groups];
    updatedGroups[updatedGroups.length - 1] = updatedLastGroup;
    
    return updatedGroups;
  }
  
  return playerCards;
};

export const moveCardBetweenGroups = (cardId, sourceGroupId, targetGroupId, playerCards) => {
  let movedCard = null;
  
  const cardsAfterRemoval = playerCards.map(card => {
    if (card.isGroup && card.id === sourceGroupId) {
      const cardIndex = card.cards.findIndex(c => c.id === cardId);
      if (cardIndex !== -1) {
        movedCard = card.cards[cardIndex];
        const updatedCards = card.cards.filter((_, index) => index !== cardIndex);
        
        return {
          ...card,
          cards: updatedCards,
          cardCount: updatedCards.length
        };
      }
    }
    return card;
  });
  
  if (!movedCard) {
    console.error('Card not found in source group');
    return playerCards;
  }
  
  const finalCards = cardsAfterRemoval.map(card => {
    if (card.isGroup && card.id === targetGroupId) {
      return {
        ...card,
        cards: [...card.cards, movedCard],
        cardCount: card.cards.length + 1
      };
    }
    return card;
  });
  
  return finalCards.filter(card => {
    if (card.isGroup && card.id === sourceGroupId && card.cards.length === 0) {
      return false;
    }
    return true;
  });
};

export const breakCardGroup = (groupId, playerCards) => {
  const groupIndex = playerCards.findIndex(card => card.id === groupId && card.isGroup);
  if (groupIndex === -1) return playerCards;
  
  const group = playerCards[groupIndex];
  const cardsBefore = playerCards.slice(0, groupIndex);
  const cardsAfter = playerCards.slice(groupIndex + 1);
  
  return [...cardsBefore, ...group.cards, ...cardsAfter];
};

export const validateAllGroups = (playerCards) => {
  const groups = playerCards.filter(card => card.isGroup);
  const validationResults = groups.map(group => ({
    groupId: group.id,
    isValid: isValidGroup(group.cards).isValid,
    cards: group.cards
  }));
  
  return validationResults;
};

// Cleanup after grouping / moving cards:
//  1. Remove groups with zero cards.
//  2. If the group a card was moved OUT of is left with fewer than 2 cards, its remaining
//     card is added to the LAST group (frees up space in the hand).
//  3. If only ONE loose (ungrouped) card remains, it is added to the LAST group.
// options.sourceGroupId      - group a card was just moved out of (optional)
// options.protectedGroupIds  - groups that must never be merged away (e.g. a group the user
//                              just created on purpose by dropping a card on empty space)
export const cleanupSingleCardGroups = (cards, options = {}) => {
  const { sourceGroupId = null, protectedGroupIds = [] } = options;
  let result = [];
  cards.forEach(card => {
    if (card.isGroup) {
      // If group has zero cards, skip it
      if (card.cards.length === 0) return;
      result.push(card);
    } else {
      result.push(card);
    }
  });

  const findLastGroupIndex = (excludeId = null) => {
    for (let i = result.length - 1; i >= 0; i--) {
      if (result[i].isGroup && result[i].id !== excludeId) return i;
    }
    return -1;
  };

  const appendToGroup = (groupIndex, cardsToAdd) => {
    const group = result[groupIndex];
    const merged = [...group.cards, ...cardsToAdd];
    result[groupIndex] = { ...group, cards: merged, cardCount: merged.length };
  };

  // (2) Source group left with fewer than 2 cards -> push remainder into the last group
  if (sourceGroupId && !protectedGroupIds.includes(sourceGroupId)) {
    const srcIdx = result.findIndex(c => c.isGroup && c.id === sourceGroupId);
    if (srcIdx !== -1 && result[srcIdx].cards.length < 2) {
      const targetIdx = findLastGroupIndex(sourceGroupId);
      if (targetIdx !== -1) {
        const leftover = result[srcIdx].cards;
        appendToGroup(targetIdx, leftover);
        result = result.filter((_, i) => i !== srcIdx);
      }
    }
  }

  // (3) Exactly one loose card left -> add it to the last group
  const looseCards = result.filter(c => !c.isGroup);
  if (looseCards.length === 1) {
    const targetIdx = findLastGroupIndex();
    if (targetIdx !== -1) {
      const loose = looseCards[0];
      appendToGroup(targetIdx, [loose]);
      result = result.filter(c => c !== loose);
    }
  }

  return result;
};